import type { RuleEditCommand, RuleSuggestion, RuleSuggestionProvider } from "../ai/RuleSuggestionProvider";
import type { DecisionEventRuleSummary } from "../domain/decisionEventTypes";
import {
    createRuleSummaryPresentation,
    type RuleSimplifiedLogicEdit,
    type RuleSimplifiedLogicEditorModel,
    type RuleSummaryPresentation,
} from "../domain/ruleSummary";
import type { DemoData, PriceToleranceRule, TaxonomyProductMapping } from "../domain/types";
import { getRuleIntegrationAdapter } from "../appglueIntegration/ruleIntegrationRegistry";
import type { ExecutableStorageBackedConfig } from "./ports/RuleIntegrationPorts";
import {
    applyRuleIntegrationSimplifiedLogicEdit,
    createRuleIntegrationConfig,
    getRuleIntegrationSummaryPresentation,
    getRuleIntegrationSimplifiedLogicEditorModel,
    hydrateRuleIntegrationConfig,
    projectRuleIntegrationDemoData,
} from "../appglueIntegration/ruleIntegrationTypes";
import {
    cloneRuleLogicDefinition,
    cloneRuleLogicDefinitionMap,
    getRuleIntegrationSourceRuleId,
    getRuleLogicDefinition,
    renderRuleLogicLines,
    SEEDED_RULE_LOGIC_BY_ID,
    type DemoRuleLogicDefinition,
    type RuleLogicDefinitionMap,
} from "../rules/ruleLogicDefinitions";
import type { DemoRule } from "../domain/ruleTypes";
import {
    cloneDemoRule,
    cloneSeededRuleMetadataMap,
    cloneSeededRuleOrder,
    type RuleMetadataMap,
} from "../rules/seedRules";
import { applyRuleRoutingEdit } from "../domain/ruleRouting";
import type { RuleOutcomeRouting } from "../rules/ruleLogicDefinitions";

export const RULE_DEFINITION_STORAGE_KEY = "appglue.electronicsDataDecisioning.ruleDefinitions.v1";
export const RULE_CATALOG_STORAGE_KEY = "appglue.electronicsDataDecisioning.ruleCatalog.v1";
export const NATIVE_EDITOR_GUARDRAIL_QUERY_PARAM = "appglueNativeEditorGuardrail";

type RuleDefinitionStorage = {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
};

export type IntegratedRuleWorkbenchEnvironment = {
    storage?: RuleDefinitionStorage;
    locationSearch?: string;
};

export type IntegratedRuleWorkbenchState = {
    ruleOrder: string[];
    ruleMetadataById: RuleMetadataMap;
    ruleDefinitionsById: RuleLogicDefinitionMap;
    editingRuleId: string | null;
    draftRuleDefinition: DemoRuleLogicDefinition | null;
    draftIntegratedRuleConfig: ExecutableStorageBackedConfig | null;
    draftIntegratedRuleStorageData: object | null;
    askSuggestion: RuleSuggestion | null;
};

export type RuleCatalogStorageV1 = {
    version: 1;
    ruleOrder: string[];
    ruleMetadataById: RuleMetadataMap;
    ruleDefinitionsById: RuleLogicDefinitionMap;
};

export type RuleCatalog = {
    ruleOrder: string[];
    ruleMetadataById: RuleMetadataMap;
    ruleDefinitionsById: RuleLogicDefinitionMap;
};

export type OpenIntegratedRuleEditorParams = {
    ruleId: string;
    priceToleranceRules: PriceToleranceRule[];
    taxonomyMappings: TaxonomyProductMapping[];
};

export type ResetIntegratedRuleDraftParams = {
    basePriceToleranceRules: PriceToleranceRule[];
    baseTaxonomyMappings: TaxonomyProductMapping[];
};

export type ApplyIntegratedRuleSuggestionParams = {
    provider: RuleSuggestionProvider;
    prompt: string;
    data: DemoData;
};

export type SaveIntegratedRuleDraftResult = {
    state: IntegratedRuleWorkbenchState;
    savedRuleId: string | null;
    projectedPriceToleranceRules?: PriceToleranceRule[];
    projectedTaxonomyMappings?: TaxonomyProductMapping[];
};

export type CreateIntegratedRuleWorkbenchViewModelParams = {
    currentPriceToleranceRules: PriceToleranceRule[];
    currentTaxonomyMappings: TaxonomyProductMapping[];
    currentRuleCounts?: DecisionEventRuleSummary;
    draftRuleCounts?: DecisionEventRuleSummary;
};

export type IntegratedRuleWorkbenchViewModel = {
    rules: DemoRule[];
    ruleOrder: string[];
    ruleMetadataById: RuleMetadataMap;
    ruleDefinitionsById: RuleLogicDefinitionMap;
    selectedRuleId: string | null;
    editingRuleId: string | null;
    draftRuleDefinition: DemoRuleLogicDefinition | null;
    draftIntegratedRuleConfig: ExecutableStorageBackedConfig | null;
    draftRuleDefinitionForEvaluation: DemoRuleLogicDefinition | null;
    askSuggestion: RuleSuggestion | null;
    canAskAppGlue: boolean;
    isEditingIntegratedRule: boolean;
    editorHostKey: string | null;
    runtimeEvidence: { runtime: "appglue_runtime"; executableType: string } | null;
    currentRuleCounts?: DecisionEventRuleSummary;
    draftRuleCounts?: DecisionEventRuleSummary;
    simplifiedLogicPresentation: RuleSummaryPresentation;
    simplifiedLogicLines: string[];
    simplifiedLogicEditorModel: RuleSimplifiedLogicEditorModel | null;
};

export const isNativeEditorGuardrailFixtureEnabled = (locationSearch = ""): boolean =>
    new URLSearchParams(locationSearch).get(NATIVE_EDITOR_GUARDRAIL_QUERY_PARAM) === "1";

const createSeededRuleCatalog = (): RuleCatalog => ({
    ruleOrder: cloneSeededRuleOrder(),
    ruleMetadataById: cloneSeededRuleMetadataMap(),
    ruleDefinitionsById: cloneRuleLogicDefinitionMap(),
});

const cloneRuleCatalog = (catalog: RuleCatalog): RuleCatalog => ({
    ruleOrder: [...catalog.ruleOrder],
    ruleMetadataById: Object.fromEntries(
        Object.entries(catalog.ruleMetadataById).map(([ruleId, rule]) => [ruleId, cloneDemoRule(rule)]),
    ),
    ruleDefinitionsById: cloneRuleLogicDefinitionMap(catalog.ruleDefinitionsById),
});

const normalizeRuleCatalog = (catalog: Partial<RuleCatalog> = {}): RuleCatalog => {
    const seeded = createSeededRuleCatalog();
    const ruleDefinitionsById: RuleLogicDefinitionMap = {
        ...seeded.ruleDefinitionsById,
        ...Object.fromEntries(
            Object.entries(catalog.ruleDefinitionsById ?? {}).map(([ruleId, definition]) => {
                const fallback = seeded.ruleDefinitionsById[ruleId];
                const legacyDefinition = definition as Partial<DemoRuleLogicDefinition> & {
                    appGlueObjectLabel?: string;
                };
                return [
                    ruleId,
                    cloneRuleLogicDefinition({
                        ...(fallback ?? legacyDefinition),
                        ...legacyDefinition,
                        ruleId,
                        integrationStatus: legacyDefinition.integrationStatus ?? fallback?.integrationStatus ?? "demo_only",
                        targetAppGlueObjectLabel:
                            legacyDefinition.targetAppGlueObjectLabel ??
                            fallback?.targetAppGlueObjectLabel ??
                            legacyDefinition.appGlueObjectLabel ??
                            "Demo-only rule",
                        conditions: legacyDefinition.conditions ?? fallback?.conditions ?? [],
                        requirements: legacyDefinition.requirements ?? fallback?.requirements ?? [],
                        actions: legacyDefinition.actions ?? fallback?.actions ?? [],
                    } as DemoRuleLogicDefinition),
                ];
            }),
        ),
    };
    const ruleMetadataById: RuleMetadataMap = {
        ...seeded.ruleMetadataById,
        ...Object.fromEntries(
            Object.entries(catalog.ruleMetadataById ?? {}).map(([ruleId, rule]) => [
                ruleId,
                cloneDemoRule({ ...rule, id: ruleId }),
            ]),
        ),
    };
    const orderedKnownRuleIds = (catalog.ruleOrder ?? seeded.ruleOrder).filter(
        (ruleId, index, ruleIds) =>
            !!ruleDefinitionsById[ruleId] && !!ruleMetadataById[ruleId] && ruleIds.indexOf(ruleId) === index,
    );
    const remainingRuleIds = Object.keys(ruleDefinitionsById).filter(
        (ruleId) => !orderedKnownRuleIds.includes(ruleId) && !!ruleMetadataById[ruleId],
    );

    return {
        ruleOrder: [...orderedKnownRuleIds, ...remainingRuleIds],
        ruleMetadataById,
        ruleDefinitionsById,
    };
};

const parseLegacyDefinitions = (saved: string | null): Partial<RuleCatalog> | null => {
    if (!saved) return null;

    try {
        return {
            ruleDefinitionsById: JSON.parse(saved) as RuleLogicDefinitionMap,
        } as Partial<RuleCatalog>;
    } catch {
        return null;
    }
};

export const loadRuleCatalog = (environment: IntegratedRuleWorkbenchEnvironment = {}): RuleCatalog => {
    try {
        const savedCatalog = environment.storage?.getItem(RULE_CATALOG_STORAGE_KEY);
        if (savedCatalog) {
            const parsed = JSON.parse(savedCatalog) as Partial<RuleCatalogStorageV1>;
            if (parsed.version === 1) return normalizeRuleCatalog(parsed);
        }

        return normalizeRuleCatalog(
            parseLegacyDefinitions(environment.storage?.getItem(RULE_DEFINITION_STORAGE_KEY) ?? null) ?? undefined,
        );
    } catch {
        return createSeededRuleCatalog();
    }
};

export const saveRuleCatalog = (
    catalog: RuleCatalog,
    environment: IntegratedRuleWorkbenchEnvironment = {},
) => {
    if (isNativeEditorGuardrailFixtureEnabled(environment.locationSearch)) return;

    const payload: RuleCatalogStorageV1 = {
        version: 1,
        ...cloneRuleCatalog(normalizeRuleCatalog(catalog)),
    };

    try {
        environment.storage?.setItem(RULE_CATALOG_STORAGE_KEY, JSON.stringify(payload));
    } catch {
        // Demo persistence is best effort; the in-memory workbench state remains authoritative for this session.
    }
};

export const loadRuleDefinitions = (environment: IntegratedRuleWorkbenchEnvironment = {}): RuleLogicDefinitionMap =>
    loadRuleCatalog(environment).ruleDefinitionsById;

export const loadInitialRuleDefinitions = (
    environment: IntegratedRuleWorkbenchEnvironment = {},
): RuleLogicDefinitionMap => {
    const loaded = loadInitialRuleCatalog(environment).ruleDefinitionsById;
    return loaded;
};

export const loadInitialRuleCatalog = (environment: IntegratedRuleWorkbenchEnvironment = {}): RuleCatalog => {
    const loaded = loadRuleCatalog(environment);
    if (!isNativeEditorGuardrailFixtureEnabled(environment.locationSearch)) return loaded;

    return {
        ...loaded,
        ruleDefinitionsById: {
            ...loaded.ruleDefinitionsById,
            "DQ-001": cloneRuleLogicDefinition({
                ...getRuleLogicDefinition(loaded.ruleDefinitionsById, "DQ-001"),
                integrationStatus: "native_appglue",
                targetAppGlueObjectLabel: "StatementRuleConfiguration + ExpressionConfiguration",
            }),
        },
    };
};

export const persistRuleDefinitions = (
    definitions: RuleLogicDefinitionMap,
    environment: IntegratedRuleWorkbenchEnvironment = {},
) => {
    if (isNativeEditorGuardrailFixtureEnabled(environment.locationSearch)) return;

    try {
        environment.storage?.setItem(RULE_DEFINITION_STORAGE_KEY, JSON.stringify(definitions));
        saveRuleCatalog(
            {
                ...createSeededRuleCatalog(),
                ruleDefinitionsById: definitions,
            },
            environment,
        );
    } catch {
        // Demo persistence is best effort; the in-memory workbench state remains authoritative for this session.
    }
};

export const createInitialIntegratedRuleWorkbenchState = (
    environment: IntegratedRuleWorkbenchEnvironment = {},
): IntegratedRuleWorkbenchState => {
    const catalog = loadInitialRuleCatalog(environment);

    return {
        ...catalog,
        editingRuleId: null,
        draftRuleDefinition: null,
        draftIntegratedRuleConfig: null,
        draftIntegratedRuleStorageData: null,
        askSuggestion: null,
    };
};

export const createRuleAdapterConfig = (
    definition: DemoRuleLogicDefinition,
    currentPriceToleranceRules?: PriceToleranceRule[],
    currentTaxonomyMappings?: TaxonomyProductMapping[],
): ExecutableStorageBackedConfig | null => {
    const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(definition));
    if (!adapter) return null;

    try {
        if (definition.appGlueStorageData) return hydrateRuleIntegrationConfig(adapter, definition.appGlueStorageData);
        return createRuleIntegrationConfig(adapter, {
            priceToleranceRules: currentPriceToleranceRules,
            taxonomyMappings: currentTaxonomyMappings,
        });
    } catch {
        return createRuleIntegrationConfig(adapter, {
            priceToleranceRules: currentPriceToleranceRules,
            taxonomyMappings: currentTaxonomyMappings,
        });
    }
};

export const renderDisplayRuleSummaryPresentation = (
    definition: DemoRuleLogicDefinition,
    config?: ExecutableStorageBackedConfig | null,
    currentPriceToleranceRules?: PriceToleranceRule[],
    currentTaxonomyMappings?: TaxonomyProductMapping[],
): RuleSummaryPresentation => {
    const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(definition));
    if (definition.integrationStatus !== "demo_only" && adapter?.ports.summaryPresenter) {
        return (
            getRuleIntegrationSummaryPresentation(
                adapter,
                config ??
                    createRuleAdapterConfig(definition, currentPriceToleranceRules, currentTaxonomyMappings) ??
                    createRuleIntegrationConfig(adapter),
                { definition },
            ) ??
            createRuleSummaryPresentation({
                status: "unsupported",
                lines: ["APPGLUE integrated rule summary unavailable"],
                sources: ["native_appglue_config"],
                warnings: ["No summary presenter returned a summary presentation."],
            })
        );
    }
    if (definition.integrationStatus !== "demo_only") {
        return createRuleSummaryPresentation({
            status: "unsupported",
            lines: ["OPEN native AppGlue editor for full expression"],
            sources: ["native_appglue_config"],
            warnings: ["Integrated rule has no summary presenter."],
        });
    }
    return createRuleSummaryPresentation({
        status: "complete",
        lines: renderRuleLogicLines(definition),
        sources: ["demo_definition"],
    });
};

export const renderDisplayRuleLogicLines = (
    definition: DemoRuleLogicDefinition,
    config?: ExecutableStorageBackedConfig | null,
    currentPriceToleranceRules?: PriceToleranceRule[],
    currentTaxonomyMappings?: TaxonomyProductMapping[],
): string[] =>
    renderDisplayRuleSummaryPresentation(
        definition,
        config,
        currentPriceToleranceRules,
        currentTaxonomyMappings,
    ).lines;

export const isRuleModified = (definition: DemoRuleLogicDefinition): boolean =>
    JSON.stringify(definition) !== JSON.stringify(SEEDED_RULE_LOGIC_BY_ID[definition.ruleId]);

export const openIntegratedRuleEditor = (
    state: IntegratedRuleWorkbenchState,
    params: OpenIntegratedRuleEditorParams,
): IntegratedRuleWorkbenchState => {
    const nextDefinition = cloneRuleLogicDefinition(getRuleLogicDefinition(state.ruleDefinitionsById, params.ruleId));
    const nextIntegratedRuleConfig =
        nextDefinition.integrationStatus !== "demo_only"
            ? createRuleAdapterConfig(nextDefinition, params.priceToleranceRules, params.taxonomyMappings)
            : null;

    return {
        ...state,
        editingRuleId: params.ruleId,
        draftRuleDefinition: nextDefinition,
        draftIntegratedRuleConfig: nextIntegratedRuleConfig,
        draftIntegratedRuleStorageData: nextIntegratedRuleConfig?.getStorageData() ?? null,
        askSuggestion: null,
    };
};

export const closeIntegratedRuleEditor = (state: IntegratedRuleWorkbenchState): IntegratedRuleWorkbenchState => ({
    ...state,
    editingRuleId: null,
    draftRuleDefinition: null,
    draftIntegratedRuleConfig: null,
    draftIntegratedRuleStorageData: null,
    askSuggestion: null,
});

export const updateIntegratedRuleDraftDefinition = (
    state: IntegratedRuleWorkbenchState,
    updater: (definition: DemoRuleLogicDefinition) => DemoRuleLogicDefinition,
): IntegratedRuleWorkbenchState => ({
    ...state,
    draftRuleDefinition: state.draftRuleDefinition ? updater(state.draftRuleDefinition) : state.draftRuleDefinition,
});

export const updateIntegratedRuleOutcomeRouting = (
    state: IntegratedRuleWorkbenchState,
    routingId: string,
    patch: Partial<Omit<RuleOutcomeRouting, "id" | "when">>,
): IntegratedRuleWorkbenchState =>
    updateIntegratedRuleDraftDefinition(state, (definition) => ({
        ...definition,
        outcomeRouting: applyRuleRoutingEdit(definition.outcomeRouting, {
            outcomeId: routingId,
            patch,
        }),
    }));

export const refreshIntegratedRuleDraftStorage = (
    state: IntegratedRuleWorkbenchState,
): IntegratedRuleWorkbenchState => ({
    ...state,
    draftIntegratedRuleStorageData:
        state.draftIntegratedRuleConfig?.getStorageData() ?? state.draftIntegratedRuleStorageData,
});

export const applyIntegratedRuleSimplifiedLogicEdit = (
    state: IntegratedRuleWorkbenchState,
    edit: RuleSimplifiedLogicEdit,
): IntegratedRuleWorkbenchState => {
    if (!state.draftRuleDefinition || !state.draftIntegratedRuleConfig) return state;

    const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(state.draftRuleDefinition));
    if (!adapter?.ports.simplifiedLogicEditor) return state;
    const summaryPresentation = getRuleIntegrationSummaryPresentation(
        adapter,
        state.draftIntegratedRuleConfig,
        { definition: state.draftRuleDefinition },
    );
    if (!summaryPresentation?.editable) return state;

    const nextIntegratedRuleConfig = applyRuleIntegrationSimplifiedLogicEdit(
        adapter,
        state.draftIntegratedRuleConfig,
        edit,
    );

    return {
        ...state,
        draftIntegratedRuleConfig: nextIntegratedRuleConfig,
        draftIntegratedRuleStorageData: nextIntegratedRuleConfig.getStorageData(),
    };
};

export const resetIntegratedRuleDraft = (
    state: IntegratedRuleWorkbenchState,
    params: ResetIntegratedRuleDraftParams,
): IntegratedRuleWorkbenchState => {
    if (!state.editingRuleId) return state;

    const seeded = SEEDED_RULE_LOGIC_BY_ID[state.editingRuleId];
    if (!seeded) return state;

    const seededDefinition = cloneRuleLogicDefinition(seeded);
    const nextIntegratedRuleConfig =
        seededDefinition.integrationStatus !== "demo_only"
            ? createRuleAdapterConfig(seededDefinition, params.basePriceToleranceRules, params.baseTaxonomyMappings)
            : null;

    return {
        ...state,
        draftRuleDefinition: seededDefinition,
        draftIntegratedRuleConfig: nextIntegratedRuleConfig,
        draftIntegratedRuleStorageData: nextIntegratedRuleConfig?.getStorageData() ?? null,
        askSuggestion: null,
    };
};

const insertRuleAfter = (ruleOrder: string[], ruleId: string, afterRuleId?: string): string[] => {
    const nextRuleOrder = ruleOrder.filter((candidateRuleId) => candidateRuleId !== ruleId);
    const insertionIndex = afterRuleId ? nextRuleOrder.indexOf(afterRuleId) : -1;

    if (insertionIndex < 0) return [...nextRuleOrder, ruleId];
    return [
        ...nextRuleOrder.slice(0, insertionIndex + 1),
        ruleId,
        ...nextRuleOrder.slice(insertionIndex + 1),
    ];
};

const nextAvailableRuleId = (baseRuleId: string, existingRuleIds: string[]): string => {
    const normalizedBase = baseRuleId.replace(/\s+/g, "-").toUpperCase();
    const existing = new Set(existingRuleIds);
    if (!existing.has(normalizedBase)) return normalizedBase;

    let suffix = 1;
    while (existing.has(`${normalizedBase}-COPY-${suffix}`)) suffix += 1;
    return `${normalizedBase}-COPY-${suffix}`;
};

export type CreateRuleParams = {
    templateRuleId?: string;
    afterRuleId?: string;
    ruleId?: string;
    name?: string;
};

export type CreateRuleResult = {
    state: IntegratedRuleWorkbenchState;
    createdRuleId: string;
};

export const createRule = (
    state: IntegratedRuleWorkbenchState,
    params: CreateRuleParams = {},
): CreateRuleResult => {
    const templateRuleId = params.templateRuleId ?? state.ruleOrder[0] ?? "DQ-001";
    const templateDefinition = cloneRuleLogicDefinition(getRuleLogicDefinition(state.ruleDefinitionsById, templateRuleId));
    const templateMetadata = state.ruleMetadataById[templateRuleId];
    const createdRuleId = nextAvailableRuleId(
        params.ruleId ?? `${templateRuleId}-CUSTOM`,
        Object.keys(state.ruleDefinitionsById),
    );
    const sourceRuleId = getRuleIntegrationSourceRuleId(templateDefinition);
    const createdDefinition = cloneRuleLogicDefinition({
        ...templateDefinition,
        ruleId: createdRuleId,
        integrationSourceRuleId: sourceRuleId,
    });
    const createdMetadata: DemoRule = cloneDemoRule({
        ...(templateMetadata ?? {
            id: templateRuleId,
            category: "Data quality",
            scope: "part",
            name: templateRuleId,
            capability: "Custom rule",
            output: "Custom rule output.",
        }),
        id: createdRuleId,
        sourceRuleId,
        name: params.name ?? `${templateMetadata?.name ?? templateRuleId} copy`,
    });

    return {
        state: {
            ...state,
            ruleOrder: insertRuleAfter(state.ruleOrder, createdRuleId, params.afterRuleId ?? templateRuleId),
            ruleMetadataById: {
                ...state.ruleMetadataById,
                [createdRuleId]: createdMetadata,
            },
            ruleDefinitionsById: {
                ...state.ruleDefinitionsById,
                [createdRuleId]: createdDefinition,
            },
        },
        createdRuleId,
    };
};

export const duplicateRule = (
    state: IntegratedRuleWorkbenchState,
    ruleId: string,
): CreateRuleResult => createRule(state, { templateRuleId: ruleId, afterRuleId: ruleId });

export type DeleteRuleResult = {
    state: IntegratedRuleWorkbenchState;
    deletedRuleId: string | null;
    replacementRuleId: string | null;
    shouldClearSelectedEvent: boolean;
};

export const deleteRule = (
    state: IntegratedRuleWorkbenchState,
    ruleId: string,
): DeleteRuleResult => {
    if (state.ruleOrder.length <= 1 || !state.ruleDefinitionsById[ruleId]) {
        return {
            state,
            deletedRuleId: null,
            replacementRuleId: state.ruleOrder[0] ?? null,
            shouldClearSelectedEvent: false,
        };
    }

    const ruleIndex = state.ruleOrder.indexOf(ruleId);
    const nextRuleOrder = state.ruleOrder.filter((candidateRuleId) => candidateRuleId !== ruleId);
    const replacementRuleId = nextRuleOrder[Math.max(0, Math.min(ruleIndex, nextRuleOrder.length - 1))] ?? null;
    const { [ruleId]: _deletedMetadata, ...ruleMetadataById } = state.ruleMetadataById;
    const shouldKeepDefinitionForAliases = Object.values(ruleMetadataById).some(
        (rule) => rule.sourceRuleId === ruleId,
    );
    const { [ruleId]: _deletedDefinition, ...remainingRuleDefinitionsById } = state.ruleDefinitionsById;
    const ruleDefinitionsById = shouldKeepDefinitionForAliases
        ? state.ruleDefinitionsById
        : remainingRuleDefinitionsById;

    return {
        state: closeIntegratedRuleEditor({
            ...state,
            ruleOrder: nextRuleOrder,
            ruleMetadataById,
            ruleDefinitionsById,
        }),
        deletedRuleId: ruleId,
        replacementRuleId,
        shouldClearSelectedEvent: true,
    };
};

export const reorderRules = (
    state: IntegratedRuleWorkbenchState,
    ruleId: string,
    direction: "up" | "down",
): IntegratedRuleWorkbenchState => {
    const currentIndex = state.ruleOrder.indexOf(ruleId);
    if (currentIndex < 0) return state;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= state.ruleOrder.length) return state;

    const nextRuleOrder = [...state.ruleOrder];
    const [movedRuleId] = nextRuleOrder.splice(currentIndex, 1);
    nextRuleOrder.splice(targetIndex, 0, movedRuleId!);

    return {
        ...state,
        ruleOrder: nextRuleOrder,
    };
};

export const updateRuleMetadata = (
    state: IntegratedRuleWorkbenchState,
    ruleId: string,
    patch: Partial<Omit<DemoRule, "id">>,
): IntegratedRuleWorkbenchState => {
    const currentRule = state.ruleMetadataById[ruleId];
    if (!currentRule) return state;

    return {
        ...state,
        ruleMetadataById: {
            ...state.ruleMetadataById,
            [ruleId]: cloneDemoRule({
                ...currentRule,
                ...patch,
                id: ruleId,
            }),
        },
    };
};

export const resetIntegratedRuleWorkbench = (): IntegratedRuleWorkbenchState => ({
    ...createSeededRuleCatalog(),
    editingRuleId: null,
    draftRuleDefinition: null,
    draftIntegratedRuleConfig: null,
    draftIntegratedRuleStorageData: null,
    askSuggestion: null,
});

const isCommandTargetingCurrentAppGlueHost = (
    command: RuleEditCommand,
    ruleDefinition: DemoRuleLogicDefinition,
    config: ExecutableStorageBackedConfig | null,
): boolean => {
    if (command.type === "createRule" || command.type === "updateRouting") return false;
    if (!config) return false;

    const sourceRuleId = getRuleIntegrationSourceRuleId(ruleDefinition);
    const adapter = getRuleIntegrationAdapter(sourceRuleId);

    return (
        (command.ruleId === ruleDefinition.ruleId || command.ruleId === sourceRuleId) &&
        adapter?.appGlueExecutableType === command.executableType &&
        adapter.ports.suggestionCommands?.supportsCommand(command, {
            ruleId: sourceRuleId,
            executableType: adapter.appGlueExecutableType,
            config,
        }) === true
    );
};

const applyRuleEditCommandToConfig = (
    command: RuleEditCommand,
    ruleDefinition: DemoRuleLogicDefinition,
    config: ExecutableStorageBackedConfig | null,
): ExecutableStorageBackedConfig | null => {
    if (command.type === "createRule" || command.type === "updateRouting") return null;
    if (!isCommandTargetingCurrentAppGlueHost(command, ruleDefinition, config)) return null;
    if (!config) return null;

    const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(ruleDefinition));
    return adapter?.ports.suggestionCommands?.applyCommand(command, config) ?? null;
};

const applyRuleEditCommandToDefinition = (
    command: RuleEditCommand,
    ruleDefinition: DemoRuleLogicDefinition,
): DemoRuleLogicDefinition | null => {
    if (command.type !== "updateRouting") return null;
    if (command.ruleId !== ruleDefinition.ruleId && command.ruleId !== getRuleIntegrationSourceRuleId(ruleDefinition)) {
        return null;
    }
    if (!ruleDefinition.outcomeRouting?.some((routing) => routing.id === command.routingId)) return null;

    return {
        ...ruleDefinition,
        outcomeRouting: applyRuleRoutingEdit(ruleDefinition.outcomeRouting, {
            outcomeId: command.routingId,
            patch: command.patch,
        }),
    };
};

export const applyIntegratedRuleSuggestion = (
    state: IntegratedRuleWorkbenchState,
    params: ApplyIntegratedRuleSuggestionParams,
): IntegratedRuleWorkbenchState => {
    if (!state.draftRuleDefinition || !params.prompt.trim()) return state;

    const suggestion = params.provider.suggest({
        prompt: params.prompt,
        data: params.data,
        currentPriceToleranceRules: params.data.priceToleranceRules,
    });
    const commandDefinition = suggestion.command
        ? applyRuleEditCommandToDefinition(suggestion.command, state.draftRuleDefinition)
        : null;
    const nextDraftRuleDefinition = commandDefinition ?? state.draftRuleDefinition;
    const commandConfig = suggestion.command
        ? applyRuleEditCommandToConfig(suggestion.command, nextDraftRuleDefinition, state.draftIntegratedRuleConfig)
        : null;
    const nextIntegratedRuleConfig = commandConfig ?? state.draftIntegratedRuleConfig;

    return {
        ...state,
        draftRuleDefinition: nextDraftRuleDefinition,
        draftIntegratedRuleConfig: nextIntegratedRuleConfig,
        draftIntegratedRuleStorageData:
            commandConfig?.getStorageData() ?? state.draftIntegratedRuleStorageData,
        askSuggestion: suggestion,
    };
};

export const getDraftRuleDefinitionForEvaluation = (
    state: IntegratedRuleWorkbenchState,
): DemoRuleLogicDefinition | null => {
    if (!state.draftRuleDefinition) return null;
    if (state.draftRuleDefinition.integrationStatus !== "demo_only" && state.draftIntegratedRuleStorageData) {
        return {
            ...state.draftRuleDefinition,
            appGlueStorageData: state.draftIntegratedRuleStorageData,
        };
    }

    return state.draftRuleDefinition;
};

export const createIntegratedRuleWorkbenchViewModel = (
    state: IntegratedRuleWorkbenchState,
    params: CreateIntegratedRuleWorkbenchViewModelParams,
): IntegratedRuleWorkbenchViewModel => {
    const draftRuleDefinitionForEvaluation = getDraftRuleDefinitionForEvaluation(state);
    const draftDefinition = draftRuleDefinitionForEvaluation ?? state.draftRuleDefinition;
    const adapter = state.draftRuleDefinition
        ? getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(state.draftRuleDefinition))
        : undefined;
    const simplifiedLogicPresentation = draftDefinition
        ? renderDisplayRuleSummaryPresentation(
              draftDefinition,
              state.draftIntegratedRuleConfig,
              params.currentPriceToleranceRules,
              params.currentTaxonomyMappings,
          )
        : createRuleSummaryPresentation({
              status: "unsupported",
              lines: [],
              sources: ["demo_definition"],
              warnings: [],
          });
    const simplifiedLogicEditorModel =
        adapter && draftDefinition && state.draftIntegratedRuleConfig && simplifiedLogicPresentation.editable
            ? getRuleIntegrationSimplifiedLogicEditorModel(adapter, state.draftIntegratedRuleConfig, {
                  definition: draftDefinition,
              })
            : null;

    return {
        rules: state.ruleOrder
            .map((ruleId) => state.ruleMetadataById[ruleId])
            .filter((rule): rule is DemoRule => !!rule),
        ruleOrder: state.ruleOrder,
        ruleMetadataById: state.ruleMetadataById,
        ruleDefinitionsById: state.ruleDefinitionsById,
        selectedRuleId: state.editingRuleId,
        editingRuleId: state.editingRuleId,
        draftRuleDefinition: state.draftRuleDefinition,
        draftIntegratedRuleConfig: state.draftIntegratedRuleConfig,
        draftRuleDefinitionForEvaluation,
        askSuggestion: state.askSuggestion,
        canAskAppGlue: supportsAskAppGlue(state),
        isEditingIntegratedRule:
            !!state.draftRuleDefinition && state.draftRuleDefinition.integrationStatus !== "demo_only",
        editorHostKey: adapter?.appGlueExecutableType ?? null,
        runtimeEvidence: adapter ? { runtime: "appglue_runtime", executableType: adapter.appGlueExecutableType } : null,
        currentRuleCounts: params.currentRuleCounts,
        draftRuleCounts: params.draftRuleCounts,
        simplifiedLogicPresentation,
        simplifiedLogicLines: simplifiedLogicPresentation.lines,
        simplifiedLogicEditorModel,
    };
};

export const saveIntegratedRuleDraft = (state: IntegratedRuleWorkbenchState): SaveIntegratedRuleDraftResult => {
    if (!state.editingRuleId || !state.draftRuleDefinition) {
        return { state, savedRuleId: null };
    }

    const currentIntegratedRuleStorageData = state.draftIntegratedRuleConfig?.getStorageData();
    const nextDefinition = {
        ...state.draftRuleDefinition,
        ruleId: state.editingRuleId,
        appGlueStorageData:
            state.draftRuleDefinition.integrationStatus !== "demo_only" && state.draftIntegratedRuleConfig
                ? (state.draftIntegratedRuleStorageData ?? currentIntegratedRuleStorageData)
                : state.draftRuleDefinition.appGlueStorageData,
    };
    const adapter = getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(nextDefinition));
    const projectedDemoData =
        adapter && state.draftIntegratedRuleConfig
            ? projectRuleIntegrationDemoData(adapter, state.draftIntegratedRuleConfig)
            : undefined;
    const savedRuleId = state.editingRuleId;

    return {
        state: closeIntegratedRuleEditor({
            ...state,
            ruleDefinitionsById: {
                ...state.ruleDefinitionsById,
                [state.editingRuleId]: nextDefinition,
            },
        }),
        savedRuleId,
        projectedPriceToleranceRules: projectedDemoData?.priceToleranceRules,
        projectedTaxonomyMappings: projectedDemoData?.taxonomyMappings,
    };
};

export const supportsAskAppGlue = (state: IntegratedRuleWorkbenchState): boolean =>
    !!state.editingRuleId &&
    !!state.draftRuleDefinition &&
    !!getRuleIntegrationAdapter(getRuleIntegrationSourceRuleId(state.draftRuleDefinition))?.ports.suggestionCommands;
